import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from 'framer-motion';
import {
  TablerCalendar,
  TablerMicrophone,
  TablerPhone,
  TablerPlus,
  TablerRotate,
  TablerSearch,
  TablerUsers,
  TablerX,
} from '../../icons';
import {patientDisplayNameFromRecord} from '../../utils/patientNames';
import {useContinueEncounter} from '../../hooks/useContinueEncounter';
import {
  isEncounterContinuable,
  isEncounterRecording,
  isPhoneEncounter,
  isTelehealthEncounter,
  toJsDate,
} from '../../services/encounterService';
import PlannerContinueSessionBanner from './PlannerContinueSessionBanner';
import UniversityGettingStartedCard from '../university/UniversityGettingStartedCard';
import UniversitySafetyBoundary from '../university/UniversitySafetyBoundary';
import usePlannerStyles, {useScrollbarSx} from './usePlannerStyles';
import usePlannerEncounterHistory from './usePlannerEncounterHistory';
import usePlannerBrowserSearch from './usePlannerBrowserSearch';
import useHydratedPlannerPatientEvents from './useHydratedPlannerPatientEvents';
import {clearPlannerEncounterHistoryPageCache} from './usePaginatedPlannerEncounterHistory';
import usePlannerRemovedSessions from './usePlannerRemovedSessions';
import usePlannerPhoneCalls, {
  clearPlannerPhoneCallsCache,
} from './usePlannerPhoneCalls';
import {patientService} from '../../services/patientService';
import {
  buildLocalPatientBrowserSections,
  PLANNER_PATIENT_VIEW_MODES,
} from './plannerPatientBrowser';
import {
  plannerEventFromEncounterView,
  plannerEventFromPhoneCall,
  plannerEventFromRemovedConversation,
  plannerRecentActivityFromPlannerEvent,
  plannerPatientFromPatientItem,
  plannerPatientToPatientItem,
} from './plannerModels';
import {
  DatePatientStack,
  PatientSectionStack,
  RecentPatientEncounterStack,
} from './PlannerBrowserStacks';
import {SearchPatientEncounterStack} from './PlannerSearchResultsStack';
import {
  PLANNER_SIDEBAR_NAME_FONT_SIZE,
  PlannerListSkeleton,
} from './PlannerSidebarList';
import {clearPlannerScheduleSnapshotCache} from './usePlannerSchedule';
import {clearScheduledBotsSnapshotCache} from '../../hooks/useScheduledBots';
import PlannerScheduleScope from './PlannerScheduleScope';
import {
  buildCompletedFilterEvents,
  buildPlannerBrowserEventTabs,
  getPlannerCompletedUnlinkedEvents,
  getPlannerSearchScheduledEvents,
  PLANNER_COMPLETED_FILTERS as COMPLETED_FILTERS,
} from './plannerBrowserEvents';
import PatientFormModal from '../common/PatientFormModal';
import usePatientCreate from '../common/usePatientCreate';
import {plannerEventDate} from './plannerDateTime';
import {
  buildPlannerRecentsDateBounds,
  formatPlannerLocalDateInput,
  isPlannerDateWithinBounds,
  plannerCustomLocalDateBounds,
  PLANNER_RECENTS_DATE_PRESET_LABELS,
  PLANNER_RECENTS_DATE_PRESETS,
} from './plannerRecentsDateFilter';

const modeLabels: Record<string, string> = {
  [PLANNER_PATIENT_VIEW_MODES.schedule]: 'Scheduled',
  [PLANNER_PATIENT_VIEW_MODES.patients]: 'Patients',
  [PLANNER_PATIENT_VIEW_MODES.completed]: 'Recent',
};

const primaryModeOptionsForEhr = [
  PLANNER_PATIENT_VIEW_MODES.schedule,
  PLANNER_PATIENT_VIEW_MODES.completed,
];

const primaryModeOptionsForLocalPatients = [
  PLANNER_PATIENT_VIEW_MODES.patients,
  PLANNER_PATIENT_VIEW_MODES.completed,
];

const primaryModeOptionsFallback = [PLANNER_PATIENT_VIEW_MODES.completed];

const completedFilterLabels = {
  [COMPLETED_FILTERS.all]: 'All',
  [COMPLETED_FILTERS.unlinked]: 'Unlinked',
  [COMPLETED_FILTERS.removed]: 'Removed',
  [COMPLETED_FILTERS.phone]: 'Company Phone',
};

// Company-shared telephonic calls (access-code lines with no user_id) currently
// flood Recents for every org member. Keep the plumbing, but hide the feed
// until calls are attributed correctly to the answering provider.
const SHOW_COMPANY_PHONE_IN_RECENTS = false;

const completedFilterOptions = SHOW_COMPANY_PHONE_IN_RECENTS
  ? Object.values(COMPLETED_FILTERS)
  : [
      COMPLETED_FILTERS.all,
      COMPLETED_FILTERS.unlinked,
      COMPLETED_FILTERS.removed,
    ];

// The "Continue recent session" banner only surfaces a session that ended
// within this window — the use case is a provider who stepped out mid-visit
// and came back to find the session auto-ended, not resuming something older
// (that still lives in the Recent tab). Kept tight to the just-stepped-out
// window so the prompt stays relevant and disappears on its own.
const CONTINUE_RECENT_WINDOW_MS = 20 * 60 * 1000;

const MODE_FADE_DURATION_MS = 120;
const MODE_FADE_IN_DELAY_MS = 60;
const SEARCH_MOVE_DURATION_MS = 200;
const SEARCH_MOVE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BROWSER_PAGE_FADE_TRANSITION = {
  duration: 0.16,
  ease: [0.25, 0.1, 0.25, 1],
};
const CONTROL_GAP = 'var(--chakra-space-1)';
const PLANNER_ENTRY_HEIGHT_PX = 72;
// Lazy-load the next page of recent encounters once the user scrolls within
// this distance of the bottom, so the list grows as they scroll instead of
// fetching everything up front.
const SCROLL_LOAD_MORE_THRESHOLD_PX = 600;
// Pull-to-refresh tuned to feel like a native list: the indicator tracks the
// finger 1:1 at first, firms up the further you pull (rubber band), and the
// spinner fades in with the gesture rather than popping in at a threshold.
const PULL_REFRESH_TRIGGER_DISTANCE = 80; // distance at which a release arms a refresh
const PULL_REFRESH_MAX_DISTANCE = 160; // elastic ceiling — plenty of over-pull headroom
const PULL_REFRESH_RESTING_DISTANCE = PLANNER_ENTRY_HEIGHT_PX; // indicator height while refreshing
const PULL_REFRESH_SETTLE_MS = 700; // how long the spinner lingers after the sync resolves
const PULL_REFRESH_RELEASE_TRANSITION_MS = 300; // smooth settle / spring-back on release
const WHEEL_PULL_RESET_MS = 180; // collapse an unfinished trackpad pull after it goes idle
const WHEEL_PULL_BURST_GAP_MS = 140; // gap that distinguishes a deliberate pull from scroll momentum

const selectedPatientName = (patient: any) => {
  return patientDisplayNameFromRecord(patient || {});
};

const selectedPatientValue = (value: any, fallback: any) =>
  String(value ?? '').trim() ? value : fallback;

const itemWithSelectedPatient = (item: any, selectedPatient: any) => {
  if (!item || !selectedPatient?.id) return item;
  const itemPatient = plannerPatientFromPatientItem(item);
  if (itemPatient?.id !== selectedPatient.id) return item;
  const selectedItem = plannerPatientToPatientItem(selectedPatient);
  if (!selectedItem) return item;
  return {
    ...item,
    ...selectedItem,
    lastSessionAt: item.lastSessionAt || null,
  };
};

const itemsWithSelectedPatient = (
  items: any[] = [],
  selectedPatient: any = null,
) =>
  selectedPatient?.id
    ? items.map(item => itemWithSelectedPatient(item, selectedPatient))
    : items;

const searchResultsWithSelectedPatient = (
  results: any[] = [],
  selectedPatient: any = null,
) => {
  if (!selectedPatient?.id) return results;
  const selectedItem = plannerPatientToPatientItem(selectedPatient);
  return results.map(result => {
    const header = result?.patientHeader;
    if (header?.patientId !== selectedPatient.id) return result;
    return {
      ...result,
      patientHeader: {
        ...header,
        name: selectedPatientName(selectedPatient) || header.name,
        language: selectedPatientValue(
          selectedPatient.language,
          header.language,
        ),
        dialect: selectedPatientValue(selectedPatient.dialect, header.dialect),
        dob: selectedPatientValue(selectedPatient.dob, header.dob),
        mrn: selectedPatientValue(selectedPatient.mrn, header.mrn),
        patient: selectedPatient,
        selectionItem: selectedItem || header.selectionItem,
      },
    };
  });
};

const markRecentEncounterDetailSelection = (event: any) => {
  if (!event || event.source === 'phoneCall') return event;
  return {...event, __plannerOpenEncounterDetail: true};
};

const resolvePullRefreshDistance = (distance: any) => {
  const rawDistance = Math.max(0, Number(distance) || 0);
  return Math.min(PULL_REFRESH_MAX_DISTANCE, rawDistance);
};

const isAtScrollTop = (element: any) => !(element?.scrollTop > 0);

// Rubber-band easing: starts ~1:1 with the finger, then asymptotes toward the
// ceiling so the pull gets progressively firmer instead of hitting a hard cap.
const applyPullResistance = (rawDelta: any) => {
  const raw = Math.max(0, Number(rawDelta) || 0);
  if (raw <= 0) return 0;
  return (
    PULL_REFRESH_MAX_DISTANCE * (1 - Math.exp(-raw / PULL_REFRESH_MAX_DISTANCE))
  );
};

const MotionBox = motion(Box);

const BrowserPageTransitionPane = ({
  children,
  pageKey,
}: Record<string, any>) => {
  const isPresent = useIsPresent();

  return (
    <MotionBox
      key={pageKey}
      data-testid="planner-browser-page"
      data-browser-page={pageKey}
      gridArea="1 / 1"
      h="100%"
      minW={0}
      minH="100%"
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
      transition={BROWSER_PAGE_FADE_TRANSITION}
      pointerEvents={isPresent ? 'auto' : 'none'}
      aria-hidden={isPresent ? undefined : true}
    >
      {children}
    </MotionBox>
  );
};

const RemovedSessionsList = ({
  events = [],
  pendingSessionIds,
  onRestore,
  styles,
}: Record<string, any>) => {
  return (
    <DatePatientStack
      cardKind="recent"
      events={events}
      emptyText="No removed sessions."
      styles={styles}
      progressive={false}
      progressiveKey="removed"
      dateGrouping="past"
      sortDirection="desc"
      getRowProps={(event: any) => {
        const session = event?.actions?.restoreSession;
        const sessionId = session?.id;
        const isPending = Boolean(
          sessionId && pendingSessionIds.has(sessionId),
        );
        const label = event?.patientName
          ? `Restore ${event.patientName} session`
          : 'Restore session';

        return {
          rowEndSlot: (
            <Tooltip label="Restore session" hasArrow openDelay={250}>
              <IconButton
                icon={<TablerRotate size={14} aria-hidden="true" />}
                aria-label={label}
                size="sm"
                variant="ghost"
                borderRadius="full"
                minW={{base: '44px', md: '36px'}}
                minH={{base: '44px', md: '36px'}}
                isLoading={isPending}
                isDisabled={isPending}
                onClick={event => {
                  event.stopPropagation();
                  onRestore?.(session);
                }}
                _hover={{bg: styles.rowHoverBg}}
                _focusVisible={styles.focusOutlineStyles}
              />
            </Tooltip>
          ),
        };
      }}
    />
  );
};

const TopModeButton = ({
  mode,
  currentMode,
  onClick,
  styles,
}: Record<string, any>) => {
  const isSelected = currentMode === mode;
  return (
    <Box
      as="button"
      type="button"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      h="44px"
      minH="44px"
      p={0}
      position="relative"
      appearance="none"
      bg="transparent"
      color={isSelected ? 'opalite.600' : styles.subtleText}
      border={0}
      borderRadius="0"
      boxShadow="none"
      cursor="pointer"
      fontFamily="inherit"
      fontSize={PLANNER_SIDEBAR_NAME_FONT_SIZE}
      fontWeight={isSelected ? 'semibold' : 'normal'}
      lineHeight="1.25"
      textAlign="center"
      whiteSpace="nowrap"
      onClick={() => onClick(mode)}
      aria-pressed={isSelected}
      _after={{
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        h: '3px',
        borderRadius: 'full',
        bg: 'opalite.600',
        opacity: isSelected ? 1 : 0,
        transform: isSelected ? 'scaleX(1)' : 'scaleX(0.72)',
        transformOrigin: 'center',
        transition: 'opacity 140ms ease, transform 140ms ease',
      }}
      _focusVisible={styles.focusOutlineStyles}
      _hover={{
        bg: 'transparent',
        color: isSelected ? 'opalite.600' : styles.textColor,
      }}
      _active={{
        bg: 'transparent',
      }}
    >
      {modeLabels[mode]}
    </Box>
  );
};

const FilterPillButton = ({
  value,
  currentValue,
  label,
  onClick,
  styles,
}: Record<string, any>) => {
  const isSelected = currentValue === value;
  return (
    <Button
      type="button"
      size="sm"
      minH={{base: '44px', md: '36px'}}
      px={3}
      borderRadius="lg"
      variant="ghost"
      bg={isSelected ? styles.listRowSelectedBg : styles.rowBg}
      color={isSelected ? styles.textColor : styles.subtleText}
      borderWidth="1px"
      borderColor={
        isSelected ? styles.listRowSelectedBorderColor : styles.rowBorderColor
      }
      boxShadow="none"
      fontSize="sm"
      fontWeight="normal"
      lineHeight="1.25"
      onClick={() => onClick(value)}
      aria-pressed={isSelected}
      _focusVisible={styles.focusOutlineStyles}
      _hover={{
        bg: isSelected ? styles.listRowSelectedBg : styles.rowHoverBg,
        color: isSelected ? styles.textColor : styles.subtleText,
      }}
      _active={{
        bg: isSelected ? styles.listRowSelectedBg : styles.rowHoverBg,
      }}
    >
      {label}
    </Button>
  );
};

const DateFilterPill = ({filter, onChange, styles}: Record<string, any>) => {
  const [customStart, setCustomStart] = useState(
    () => filter.customStart || formatPlannerLocalDateInput(new Date()),
  );
  const [customEnd, setCustomEnd] = useState(
    () => filter.customEnd || formatPlannerLocalDateInput(new Date()),
  );
  const [showCustom, setShowCustom] = useState(
    filter.preset === PLANNER_RECENTS_DATE_PRESETS.custom,
  );
  const customBounds = plannerCustomLocalDateBounds(customStart, customEnd);
  const customLabel =
    filter.customStart && filter.customEnd
      ? `${new Date(`${filter.customStart}T00:00:00`).toLocaleDateString(
          undefined,
          {month: 'short', day: 'numeric'},
        )} – ${new Date(`${filter.customEnd}T00:00:00`).toLocaleDateString(
          undefined,
          {month: 'short', day: 'numeric'},
        )}`
      : 'Custom Range';
  const label =
    filter.preset === PLANNER_RECENTS_DATE_PRESETS.custom
      ? customLabel
      : (PLANNER_RECENTS_DATE_PRESET_LABELS as Record<string, string>)[
          String(filter.preset || '')
        ] || 'All Time';

  return (
    <Popover placement="bottom-end" isLazy>
      {({onClose}) => (
        <>
          <PopoverTrigger>
            <Button
              type="button"
              size="sm"
              minH={{base: '44px', md: '36px'}}
              px={3}
              flexShrink={0}
              borderRadius="lg"
              variant="ghost"
              bg={
                filter.preset === PLANNER_RECENTS_DATE_PRESETS.allTime
                  ? styles.rowBg
                  : styles.listRowSelectedBg
              }
              color={styles.subtleText}
              borderWidth="1px"
              borderColor={
                filter.preset === PLANNER_RECENTS_DATE_PRESETS.allTime
                  ? styles.rowBorderColor
                  : styles.listRowSelectedBorderColor
              }
              boxShadow="none"
              fontSize="sm"
              fontWeight="normal"
              lineHeight="1.25"
              leftIcon={<TablerCalendar size={15} aria-hidden="true" />}
              aria-label={`Date filter: ${label}`}
              _focusVisible={styles.focusOutlineStyles}
              _hover={{bg: styles.rowHoverBg, color: styles.subtleText}}
              _active={{bg: styles.rowHoverBg}}
            >
              {label}
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              w={{base: 'calc(100vw - 2rem)', sm: '20rem'}}
              maxW="20rem"
              bg={styles.rowBg}
              borderColor={styles.rowBorderColor}
              boxShadow={styles.cardShadow}
              _focusVisible={styles.focusOutlineStyles}
            >
              <PopoverHeader fontWeight="semibold">Session dates</PopoverHeader>
              <PopoverBody>
                <Flex direction="column" gap={2}>
                  {Object.values(PLANNER_RECENTS_DATE_PRESETS)
                    .filter(
                      preset => preset !== PLANNER_RECENTS_DATE_PRESETS.custom,
                    )
                    .map(preset => (
                      <Button
                        key={preset}
                        type="button"
                        variant="ghost"
                        justifyContent="flex-start"
                        minH="44px"
                        aria-pressed={filter.preset === preset}
                        onClick={() => {
                          setShowCustom(false);
                          onChange({
                            preset,
                            ...buildPlannerRecentsDateBounds(preset),
                          });
                          onClose();
                        }}
                      >
                        {PLANNER_RECENTS_DATE_PRESET_LABELS[preset]}
                      </Button>
                    ))}
                  <Button
                    type="button"
                    variant="ghost"
                    justifyContent="flex-start"
                    minH="44px"
                    aria-expanded={showCustom}
                    onClick={() => setShowCustom(true)}
                  >
                    Custom Range
                  </Button>
                  {showCustom && (
                    <Flex direction="column" gap={3} pt={1}>
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>
                          Start date
                        </FormLabel>
                        <Input
                          type="date"
                          value={customStart}
                          minH="44px"
                          onChange={event => setCustomStart(event.target.value)}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm" mb={1}>
                          End date
                        </FormLabel>
                        <Input
                          type="date"
                          value={customEnd}
                          minH="44px"
                          onChange={event => setCustomEnd(event.target.value)}
                        />
                      </FormControl>
                      <Button
                        type="button"
                        minH="44px"
                        bg="opalite.600"
                        color="white"
                        isDisabled={!customBounds}
                        onClick={() => {
                          if (!customBounds) return;
                          onChange({
                            preset: PLANNER_RECENTS_DATE_PRESETS.custom,
                            customStart,
                            customEnd,
                            ...customBounds,
                          });
                          onClose();
                        }}
                        _hover={{bg: 'opalite.700'}}
                      >
                        Apply
                      </Button>
                    </Flex>
                  )}
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
};

const SearchProgressBand = ({styles}: Record<string, any>) => (
  <Flex
    data-testid="planner-search-progress-band"
    role="status"
    aria-live="polite"
    aria-busy="true"
    align="center"
    justify="space-between"
    flex="0 0 auto"
    minH="32px"
    w="100%"
    px={3}
    py={1.5}
    color={styles.metaTextColor}
    fontSize="sm"
    fontWeight="normal"
    lineHeight="1.25"
  >
    <Text>Searching...</Text>
    <Spinner
      data-testid="planner-search-progress-spinner"
      color="opalite.600"
      size="sm"
      thickness="2px"
      speed="0.7s"
    />
  </Flex>
);

const SearchEmptyState = ({
  isLoading = false,
  message,
  styles,
}: Record<string, any>) => (
  <Flex
    data-testid="planner-search-empty-state"
    align="center"
    justify="center"
    direction="column"
    gap={3}
    h="100%"
    minH="100%"
    py={10}
    textAlign="center"
  >
    <Flex
      data-testid="planner-search-state-icon-slot"
      align="center"
      justify="center"
      boxSize="44px"
      color={isLoading ? 'opalite.600' : styles.metaTextColor}
      flex="0 0 44px"
    >
      {isLoading ? (
        <Spinner
          data-testid="planner-search-loading-spinner"
          boxSize="40px"
          color="currentColor"
          thickness="2px"
          speed="0.7s"
        />
      ) : (
        <TablerSearch
          data-testid="planner-search-empty-icon"
          size={40}
          strokeWidth={1.75}
          color="currentColor"
        />
      )}
    </Flex>
    <Text
      data-testid="planner-search-state-message"
      color={styles.metaTextColor}
      fontSize="md"
      fontWeight="normal"
      lineHeight="1.4"
      minH="1.4em"
      display="flex"
      alignItems="center"
    >
      {message}
    </Text>
  </Flex>
);

const PlannerBrowserColumn = ({
  sections = [],
  scheduleScope = null,
  isLoading = false,
  hasLoaded = false,
  error = null,
  isFallback = false,
  userData = null,
  onSelect,
  onSelectPatient,
  onNewEncounter,
  onNewTelephoneEncounter,
  onNewDictation,
  refreshSchedule,
  refreshEhrConnection,
  encounterHistorySeed = null,
  recentsFeedRevision = 0,
  selectedEventId = null,
  selectedPatientId = null,
  selectedPatient = null,
  deletedPatientIds = null,
  containerDisplay = undefined,
  compactHeader = null,
}: Record<string, any>) => {
  const styles = usePlannerStyles();
  const scrollbarSx = useScrollbarSx();
  const toast = useToast();
  const {continueEncounter, isContinuing, continueModePicker} =
    useContinueEncounter();
  const prefersReducedMotion = useReducedMotion();
  const searchInputRef = useRef<any>(null);
  const scrollContainerRef = useRef<any>(null);
  const pullStartYRef = useRef<any>(null);
  const pullDistanceRef = useRef(0);
  const pullWheelDistanceRef = useRef(0);
  const pullWheelArmedRef = useRef(false);
  const pullWheelLastTimeRef = useRef(0);
  const pullWheelResetTimerRef = useRef<any>(null);
  const pullRefreshSettleTimerRef = useRef<any>(null);
  const [viewMode, setViewMode] = useState<any>(
    PLANNER_PATIENT_VIEW_MODES.schedule,
  );
  const [completedFilter, setCompletedFilter] = useState<any>(
    COMPLETED_FILTERS.all,
  );
  const [dateFilter, setDateFilter] = useState<any>(() => ({
    preset: PLANNER_RECENTS_DATE_PRESETS.allTime,
    ...buildPlannerRecentsDateBounds(PLANNER_RECENTS_DATE_PRESETS.allTime),
  }));
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pullDistance, setPullDistance] = useState(0);
  const [isPullRefreshing, setIsPullRefreshing] = useState(false);
  const [isPhonePrefetchReady, setIsPhonePrefetchReady] = useState(false);
  const [pendingRemovedSessionIds, setPendingRemovedSessionIds] = useState(
    () => new Set(),
  );
  const [dismissedContinueId, setDismissedContinueId] = useState<any>(null);
  const handlePatientCreated = useCallback(
    (created: any, patientItem: any) => {
      if (patientItem) {
        onSelectPatient?.(patientItem, 'created');
      }
    },
    [onSelectPatient],
  );
  // "Use existing" on the duplicate-create warning: select the matched
  // patient exactly like a fresh create would, just without creating.
  const handleUseExistingPatient = useCallback(
    (patient: any, patientItem: any) => {
      if (patientItem) {
        onSelectPatient?.(patientItem, 'existing');
      }
    },
    [onSelectPatient],
  );
  const {
    isCreateModalOpen,
    openCreateModal,
    closeCreateModal,
    formData,
    setFormData,
    saving: isCreatingPatient,
    handleCreate: handleCreatePatient,
    duplicateMatch,
    confirmDuplicateCreate,
    chooseExistingPatient,
  } = usePatientCreate({
    userData,
    onCreated: handlePatientCreated,
    onUseExisting: handleUseExistingPatient,
  });
  const trimmedSearchQuery = searchQuery.trim();
  const hasSearchQuery = trimmedSearchQuery.length > 0;
  const isSearchOpen = isSearchActive || hasSearchQuery;
  const userId = userData?.userId || null;
  const companyId = userData?.companyId || null;
  const facilityId = userData?.facilityId || null;
  const shouldLoadEncounterHistory =
    (viewMode === PLANNER_PATIENT_VIEW_MODES.completed &&
      completedFilter !== COMPLETED_FILTERS.removed) ||
    viewMode === PLANNER_PATIENT_VIEW_MODES.patients ||
    hasSearchQuery;
  const historyDateBounds = hasSearchQuery
    ? {startDate: null, endDate: null}
    : dateFilter;
  useEffect(() => {
    if (!companyId) {
      setIsPhonePrefetchReady(false);
      return undefined;
    }
    if (viewMode === PLANNER_PATIENT_VIEW_MODES.completed) {
      setIsPhonePrefetchReady(true);
      return undefined;
    }

    let cancelled = false;
    const markReady = () => {
      if (!cancelled) setIsPhonePrefetchReady(true);
    };
    const win = typeof window === 'undefined' ? null : (window as any);
    if (win?.requestIdleCallback) {
      const idleId = win.requestIdleCallback(markReady, {timeout: 1500});
      return () => {
        cancelled = true;
        win.cancelIdleCallback?.(idleId);
      };
    }

    const timerId = setTimeout(markReady, 900);
    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [companyId, viewMode]);
  const shouldLoadPhoneCalls = Boolean(
    SHOW_COMPANY_PHONE_IN_RECENTS &&
    companyId &&
    (viewMode === PLANNER_PATIENT_VIEW_MODES.completed || isPhonePrefetchReady),
  );
  useEffect(() => {
    if (
      !SHOW_COMPANY_PHONE_IN_RECENTS &&
      completedFilter === COMPLETED_FILTERS.phone
    ) {
      setCompletedFilter(COMPLETED_FILTERS.all);
    }
  }, [completedFilter]);
  const shouldLoadRemovedSessions =
    viewMode === PLANNER_PATIENT_VIEW_MODES.completed &&
    completedFilter === COMPLETED_FILTERS.removed;
  const {
    encounters: historyEncounters,
    hasMore: hasMoreHistory,
    hasLoaded: hasLoadedHistory,
    loadingMore: loadingMoreHistory,
    loadMore: loadMoreHistory,
    refresh: refreshEncounterHistory,
  } = usePlannerEncounterHistory({
    userId,
    companyId,
    enabled: Boolean(userId && shouldLoadEncounterHistory),
    initialSeed:
      historyDateBounds.startDate || historyDateBounds.endDate
        ? null
        : encounterHistorySeed,
    startDate: historyDateBounds.startDate,
    endDate: historyDateBounds.endDate,
    refreshKey: recentsFeedRevision,
  });
  const {
    calls: phoneCalls,
    hasMore: hasMorePhoneCalls,
    hasLoaded: hasLoadedPhoneCalls,
    loadingMore: loadingMorePhoneCalls,
    loadMore: loadMorePhoneCalls,
  } = usePlannerPhoneCalls({
    companyId,
    userId,
    enabled: shouldLoadPhoneCalls,
  });
  const {
    sessions: removedSessions,
    hasLoaded: hasLoadedRemovedSessions,
    refresh: refreshRemovedSessions,
    setSessions: setRemovedSessions,
  } = usePlannerRemovedSessions({
    userId,
    enabled: Boolean(userId && shouldLoadRemovedSessions),
    startDate: dateFilter.startDate,
    endDate: dateFilter.endDate,
    refreshKey: recentsFeedRevision,
  });
  const rawHistoryEvents = useMemo(
    () =>
      historyEncounters
        .map((encounter: any) =>
          plannerEventFromEncounterView(encounter, userId),
        )
        .filter(Boolean),
    [historyEncounters, userId],
  );
  const historyEvents = useHydratedPlannerPatientEvents(
    rawHistoryEvents,
    companyId,
    Boolean(companyId),
    {facilityId},
  );
  const rawSectionEvents = useMemo(
    () => sections.flatMap((section: any) => section.events || []),
    [sections],
  );
  const hydratedSectionEvents = useHydratedPlannerPatientEvents(
    rawSectionEvents,
    companyId,
    Boolean(companyId),
    {facilityId},
  );
  const hydratedSections = useMemo(() => {
    if (!hydratedSectionEvents.length) return sections;
    const eventById = new Map(
      hydratedSectionEvents
        .filter((event: any) => event?.id)
        .map((event: any) => [event.id, event]),
    );
    return sections.map((section: any) => ({
      ...section,
      events: (section.events || []).map(
        (event: any) => eventById.get(event?.id) || event,
      ),
    }));
  }, [hydratedSectionEvents, sections]);
  const removedEvents = useMemo(
    () =>
      removedSessions
        .map((conversation: any) =>
          plannerEventFromRemovedConversation(conversation),
        )
        .filter(Boolean),
    [removedSessions],
  );
  const phoneEvents = useMemo(
    () =>
      SHOW_COMPANY_PHONE_IN_RECENTS
        ? phoneCalls
            .map((call: any) => plannerEventFromPhoneCall(call))
            .filter(Boolean)
        : [],
    [phoneCalls],
  );
  const eventTabs = useMemo(
    () =>
      buildPlannerBrowserEventTabs({
        historyEvents,
        phoneEvents,
        sections: hydratedSections,
      }),
    [historyEvents, phoneEvents, hydratedSections],
  );

  // The single most-recent session worth offering a one-tap resume. Read
  // straight from the schedule's encounter-history seed (already fetched and
  // hydrated regardless of the active tab) so the banner works on Scheduled
  // without triggering the gated `usePlannerEncounterHistory` load. We only
  // surface in-person, closed-but-continuable sessions from the last few hours
  // — phone/telehealth run on pipelines the Continue flow can't resume, and a
  // stale session belongs in the Recent tab, not a "pick up where you left
  // off" prompt.
  const continueCandidate = useMemo(() => {
    const candidates = encounterHistorySeed?.encounters || [];
    if (!candidates.length) return null;
    const cutoff = Date.now() - CONTINUE_RECENT_WINDOW_MS;
    let best: any = null;
    let bestTime = -Infinity;
    candidates.forEach((encounter: any) => {
      if (!encounter) return;
      if (!isEncounterContinuable(encounter)) return;
      if (isEncounterRecording(encounter)) return;
      if (isPhoneEncounter(encounter) || isTelehealthEncounter(encounter)) {
        return;
      }
      const ref = encounter.end_time || encounter.start_time;
      const refDate = ref instanceof Date ? ref : toJsDate(ref);
      const refTime = refDate ? refDate.getTime() : NaN;
      if (!Number.isFinite(refTime) || refTime < cutoff) return;
      if (refTime > bestTime) {
        best = encounter;
        bestTime = refTime;
      }
    });
    return best;
  }, [encounterHistorySeed]);

  const canPullRefresh = typeof refreshEhrConnection === 'function';

  const clearPullRefreshSettleTimer = useCallback(() => {
    if (!pullRefreshSettleTimerRef.current) return;
    clearTimeout(pullRefreshSettleTimerRef.current);
    pullRefreshSettleTimerRef.current = null;
  }, []);

  const clearWheelPullResetTimer = useCallback(() => {
    if (!pullWheelResetTimerRef.current) return;
    clearTimeout(pullWheelResetTimerRef.current);
    pullWheelResetTimerRef.current = null;
  }, []);

  const updatePullDistance = useCallback((distance: any) => {
    const nextDistance = resolvePullRefreshDistance(distance);
    pullDistanceRef.current = nextDistance;
    setPullDistance(nextDistance);
  }, []);

  const resetPullDistance = useCallback(() => {
    pullStartYRef.current = null;
    pullWheelDistanceRef.current = 0;
    pullWheelArmedRef.current = false;
    updatePullDistance(0);
  }, [updatePullDistance]);

  const triggerPullRefresh = useCallback(async () => {
    if (!canPullRefresh || isPullRefreshing) return;
    clearWheelPullResetTimer();
    resetPullDistance();
    clearPullRefreshSettleTimer();
    setIsPullRefreshing(true);
    try {
      await refreshEhrConnection();
      clearPlannerEncounterHistoryPageCache();
      clearPlannerPhoneCallsCache();
      clearPlannerScheduleSnapshotCache();
      clearScheduledBotsSnapshotCache();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          '[PlannerBrowserColumn] pull-to-refresh EHR sync failed:',
          err?.message || err,
        );
      }
    } finally {
      pullRefreshSettleTimerRef.current = setTimeout(() => {
        pullRefreshSettleTimerRef.current = null;
        setIsPullRefreshing(false);
      }, PULL_REFRESH_SETTLE_MS);
    }
  }, [
    canPullRefresh,
    clearPullRefreshSettleTimer,
    clearWheelPullResetTimer,
    isPullRefreshing,
    refreshEhrConnection,
    resetPullDistance,
  ]);

  // Pull/scroll-to-refresh syncs the EHR schedule, so it only belongs on the
  // Scheduled tab. On Patients and Recent (the only tabs a non-EHR account
  // sees) the gesture is suppressed entirely.
  const isPullRefreshAvailable =
    canPullRefresh &&
    !isPullRefreshing &&
    !isLoading &&
    !isSearchOpen &&
    viewMode === PLANNER_PATIENT_VIEW_MODES.schedule;

  const scheduleWheelPullReset = useCallback(() => {
    clearWheelPullResetTimer();
    pullWheelResetTimerRef.current = setTimeout(() => {
      pullWheelResetTimerRef.current = null;
      resetPullDistance();
    }, WHEEL_PULL_RESET_MS);
  }, [clearWheelPullResetTimer, resetPullDistance]);

  const handlePullTouchStart = useCallback(
    (event: any) => {
      if (!isPullRefreshAvailable || !isAtScrollTop(event.currentTarget))
        return;
      const touch = event.touches?.[0];
      if (!touch) return;
      pullStartYRef.current = touch.clientY;
    },
    [isPullRefreshAvailable],
  );

  const handlePullTouchMove = useCallback(
    (event: any) => {
      if (!isPullRefreshAvailable || pullStartYRef.current == null) return;
      if (!isAtScrollTop(event.currentTarget)) {
        resetPullDistance();
        return;
      }
      const touch = event.touches?.[0];
      if (!touch) return;
      const deltaY = touch.clientY - pullStartYRef.current;
      if (deltaY <= 0) {
        updatePullDistance(0);
        return;
      }
      event.preventDefault();
      updatePullDistance(applyPullResistance(deltaY));
    },
    [isPullRefreshAvailable, resetPullDistance, updatePullDistance],
  );

  const handlePullTouchEnd = useCallback(() => {
    if (pullDistanceRef.current >= PULL_REFRESH_TRIGGER_DISTANCE) {
      triggerPullRefresh();
      return;
    }
    resetPullDistance();
  }, [resetPullDistance, triggerPullRefresh]);

  const handlePullWheel = useCallback(
    (event: any) => {
      if (!isPullRefreshAvailable) return;
      // When this list isn't its own scroller (the narrow/base layout scrolls
      // the page instead), don't hijack the wheel for a pull gesture — letting
      // it pass through is what lets the user scroll by hovering over the list.
      const node = event.currentTarget;
      if (node && node.scrollHeight - node.clientHeight <= 1) return;
      const now = Date.now();
      const sinceLastWheel = now - pullWheelLastTimeRef.current;
      pullWheelLastTimeRef.current = now;
      if (!isAtScrollTop(event.currentTarget) || event.deltaY >= 0) {
        pullWheelArmedRef.current = false;
        pullWheelDistanceRef.current = 0;
        updatePullDistance(0);
        return;
      }
      // Only a burst that begins after a pause counts as a deliberate pull; the
      // continuous momentum tail of a scroll-to-top has no gap, so it stays inert.
      if (sinceLastWheel > WHEEL_PULL_BURST_GAP_MS) {
        pullWheelArmedRef.current = true;
        pullWheelDistanceRef.current = 0;
      }
      if (!pullWheelArmedRef.current) {
        scheduleWheelPullReset();
        return;
      }
      event.preventDefault();
      const rawDistance = pullWheelDistanceRef.current + Math.abs(event.deltaY);
      pullWheelDistanceRef.current = rawDistance;
      updatePullDistance(applyPullResistance(rawDistance));
      if (pullDistanceRef.current >= PULL_REFRESH_TRIGGER_DISTANCE) {
        triggerPullRefresh();
        return;
      }
      scheduleWheelPullReset();
    },
    [
      isPullRefreshAvailable,
      scheduleWheelPullReset,
      triggerPullRefresh,
      updatePullDistance,
    ],
  );

  useEffect(
    () => () => {
      clearPullRefreshSettleTimer();
      clearWheelPullResetTimer();
    },
    [clearPullRefreshSettleTimer, clearWheelPullResetTimer],
  );

  // React binds wheel/touchmove as passive at the root, so preventDefault in a
  // synthetic handler is a no-op (and warns every frame). Bind them natively as
  // non-passive so a pull can actually suppress the browser's own overscroll
  // bounce and own the gesture cleanly.
  useEffect(() => {
    const node = scrollContainerRef.current;
    if (!node) return undefined;
    node.addEventListener('wheel', handlePullWheel, {passive: false});
    node.addEventListener('touchmove', handlePullTouchMove, {passive: false});
    return () => {
      node.removeEventListener('wheel', handlePullWheel);
      node.removeEventListener('touchmove', handlePullTouchMove);
    };
  }, [handlePullWheel, handlePullTouchMove]);

  useEffect(() => {
    if (isSearchActive) {
      searchInputRef.current?.focus();
    }
  }, [isSearchActive]);

  const markRemovedSessionPending = useCallback(
    (sessionId: any, pending: any) => {
      setPendingRemovedSessionIds(prev => {
        const next = new Set(prev);
        if (pending) next.add(sessionId);
        else next.delete(sessionId);
        return next;
      });
    },
    [],
  );

  const handleRestoreRemovedSession = useCallback(
    async (session: any) => {
      if (!session?.id) return;
      markRemovedSessionPending(session.id, true);
      const previouslyRemoved = removedSessions;
      setRemovedSessions((prev: any) =>
        prev.filter((s: any) => s.id !== session.id),
      );
      try {
        await patientService.restoreSession(session.id);
        toast({title: 'Session restored', status: 'success', duration: 2500});
        clearPlannerEncounterHistoryPageCache();
        await Promise.all([
          refreshRemovedSessions(),
          Promise.resolve(refreshEncounterHistory?.()),
          Promise.resolve(refreshSchedule?.()),
        ]);
      } catch (err) {
        console.error('Failed to restore session:', err);
        setRemovedSessions(previouslyRemoved);
        toast({
          title: 'Could not restore session',
          status: 'error',
          duration: 3000,
        });
      } finally {
        markRemovedSessionPending(session.id, false);
      }
    },
    [
      markRemovedSessionPending,
      refreshEncounterHistory,
      refreshRemovedSessions,
      refreshSchedule,
      removedSessions,
      setRemovedSessions,
      toast,
    ],
  );

  const activateSearch = () => {
    setIsSearchActive(true);
  };

  const closeSearch = () => {
    setSearchQuery('');
    setIsSearchActive(false);
    searchInputRef.current?.blur();
  };

  const handleViewModeClick = (mode: any) => {
    setSearchQuery('');
    setIsSearchActive(false);
    searchInputRef.current?.blur();
    setViewMode(mode);
  };

  const handleSelectPatient = (item: any, source: any) => {
    onSelectPatient?.(item, source);
  };

  const scheduleEvents = useMemo(
    () => eventTabs[PLANNER_PATIENT_VIEW_MODES.schedule] || [],
    [eventTabs],
  );
  const searchScheduledEvents = useMemo(
    () => getPlannerSearchScheduledEvents(hydratedSections),
    [hydratedSections],
  );
  const completedEvents = useMemo(
    () => eventTabs[PLANNER_PATIENT_VIEW_MODES.completed] || [],
    [eventTabs],
  );
  const dateFilteredCompletedEvents = useMemo(
    () =>
      completedEvents.filter((event: any) =>
        isPlannerDateWithinBounds(plannerEventDate(event), dateFilter),
      ),
    [completedEvents, dateFilter],
  );
  const phoneModeEvents = useMemo(
    () => eventTabs[PLANNER_PATIENT_VIEW_MODES.phone] || [],
    [eventTabs],
  );
  const completedUnlinkedEvents = useMemo(
    () => getPlannerCompletedUnlinkedEvents(dateFilteredCompletedEvents),
    [dateFilteredCompletedEvents],
  );
  const completedAllEvents = useMemo(
    () =>
      buildCompletedFilterEvents({
        completedEvents: dateFilteredCompletedEvents,
        completedFilter: COMPLETED_FILTERS.all,
        phoneModeEvents,
      }),
    [dateFilteredCompletedEvents, phoneModeEvents],
  );
  const completedRecentActivities = useMemo(
    () =>
      dateFilteredCompletedEvents
        .map((event: any) => plannerRecentActivityFromPlannerEvent(event))
        .filter(Boolean),
    [dateFilteredCompletedEvents],
  );
  const completedAllRecentItems = useMemo(
    () => [...completedRecentActivities, ...phoneModeEvents],
    [completedRecentActivities, phoneModeEvents],
  );
  const completedFilterEvents = useMemo(
    () =>
      buildCompletedFilterEvents({
        completedEvents: dateFilteredCompletedEvents,
        completedFilter,
        completedUnlinkedEvents,
        phoneModeEvents,
      }),
    [
      dateFilteredCompletedEvents,
      completedFilter,
      completedUnlinkedEvents,
      phoneModeEvents,
    ],
  );
  const {
    combinedSearchError,
    deferredSearchQuery,
    hasActiveEhr,
    hasLoadedEhrConfig,
    hasAnySearchResults,
    isLoadingCompanySearchEncounters,
    isLoadingLocalPatients,
    isLoadingSearchResults,
    isSearchingEhrPatients,
    localPatientItems,
    localPatientsError,
    searchResults,
  } = usePlannerBrowserSearch({
    completedAllEvents,
    hasSearchQuery,
    scheduleEvents: searchScheduledEvents,
    searchQuery,
    userData,
  });
  // Drop patients deleted this session so a just-removed patient can't linger
  // in the roster/search, get re-selected, and trigger a duplicate delete.
  const prunePatientItems = useCallback(
    (items: any[]) => {
      if (!deletedPatientIds || deletedPatientIds.size === 0) return items;
      return (items || []).filter((item: any) => {
        const localId = item?.localPatient?.id;
        const rowId = item?.id;
        return !(
          (localId && deletedPatientIds.has(String(localId))) ||
          (rowId && deletedPatientIds.has(String(rowId)))
        );
      });
    },
    [deletedPatientIds],
  );
  const prunedSearchResults = useMemo(
    () => prunePatientItems(searchResults),
    [prunePatientItems, searchResults],
  );
  const prunedLocalPatientItems = useMemo(
    () => prunePatientItems(localPatientItems),
    [prunePatientItems, localPatientItems],
  );
  const displaySearchResults = useMemo(
    () =>
      searchResultsWithSelectedPatient(prunedSearchResults, selectedPatient),
    [prunedSearchResults, selectedPatient],
  );
  const displayLocalPatientItems = useMemo(
    () => itemsWithSelectedPatient(prunedLocalPatientItems, selectedPatient),
    [prunedLocalPatientItems, selectedPatient],
  );
  const localPatientBrowserBuckets = useMemo(
    () =>
      buildLocalPatientBrowserSections(displayLocalPatientItems, historyEvents),
    [displayLocalPatientItems, historyEvents],
  );
  const localPatientBrowserSections = useMemo(
    () => [
      // Progressive mount the roster (the A-to-Z bucket can be very large) so
      // opening Patients fills in over a few frames instead of one synchronous
      // commit; preserveVisibleCountOnChange keeps mounted rows put as the
      // roster hydrates.
      ...(localPatientBrowserBuckets.recentItems.length
        ? [
            {
              key: 'recent',
              label: 'Recent',
              items: localPatientBrowserBuckets.recentItems,
              progressive: true,
              preserveVisibleCountOnChange: true,
            },
          ]
        : []),
      ...(localPatientBrowserBuckets.alphabeticalItems.length
        ? [
            {
              key: 'a-to-z',
              label: 'A to Z',
              items: localPatientBrowserBuckets.alphabeticalItems,
              progressive: true,
              preserveVisibleCountOnChange: true,
            },
          ]
        : []),
    ],
    [localPatientBrowserBuckets],
  );
  const canShowLocalPatientsMode = Boolean(
    userData?.userId && hasLoadedEhrConfig && hasActiveEhr !== true,
  );
  const canShowScheduledMode = hasActiveEhr === true;
  // Until the EHR config resolves we can't know whether this account should
  // default to Scheduled (EHR) or Patients, so hold the primary tabs back
  // rather than briefly committing to Recent (the old fallback) and stranding
  // non-EHR accounts there once the config loads.
  const primaryModeOptions = !hasLoadedEhrConfig
    ? null
    : canShowScheduledMode
      ? primaryModeOptionsForEhr
      : canShowLocalPatientsMode
        ? primaryModeOptionsForLocalPatients
        : primaryModeOptionsFallback;
  const effectiveViewMode = primaryModeOptions
    ? primaryModeOptions.includes(viewMode)
      ? viewMode
      : primaryModeOptions[0]
    : viewMode;
  const isResolvingPrimaryMode = !hasSearchQuery && !primaryModeOptions;
  const canCreatePatient = Boolean(
    userData?.userId && userData?.facilityId && hasActiveEhr !== true,
  );
  // The "+" becomes a menu when there is more than one create action: New
  // Patient (non-EHR practices), New Telephone Encounter (EHR-linked), or
  // Dictation (scribe-enabled users).
  const showNewMenu =
    canCreatePatient ||
    Boolean(onNewTelephoneEncounter) ||
    Boolean(onNewDictation);
  const isInitialScheduleLoading = isLoading && !hasLoaded && !error;
  const statusMessage = error
    ? isFallback
      ? 'Showing sample schedule. Live schedule could not load.'
      : error
    : '';
  const browserPageKey =
    isSearchOpen || hasSearchQuery
      ? 'search'
      : isResolvingPrimaryMode
        ? 'loading'
        : effectiveViewMode === PLANNER_PATIENT_VIEW_MODES.completed
          ? 'recent'
          : effectiveViewMode === PLANNER_PATIENT_VIEW_MODES.patients
            ? 'patients'
            : 'scheduled';
  const shouldAnimateBrowserPage = Boolean(
    !prefersReducedMotion &&
    !isSearchOpen &&
    !hasSearchQuery &&
    !isLoadingSearchResults,
  );

  useEffect(() => {
    if (!primaryModeOptions) return;
    if (viewMode === effectiveViewMode) return;
    setViewMode(effectiveViewMode);
  }, [primaryModeOptions, effectiveViewMode, viewMode]);

  // Hold the Recent view on a skeleton until every source its current filter
  // draws from has delivered its first page. Painting earlier either flashes
  // the empty-state text mid-flight or paints history rows and then reshuffles
  // them when phone calls merge in. Deliberately not tied to the fetch enable
  // flags — those lag the effective mode by a frame, and "not delivered yet"
  // should read as loading in that window too. Both sources sit behind module
  // caches, so this gate only bites on a true cold load.
  const isHistoryPending = Boolean(
    userId &&
    completedFilter !== COMPLETED_FILTERS.removed &&
    !hasLoadedHistory,
  );
  const isRemovedPending = Boolean(
    userId &&
    completedFilter === COMPLETED_FILTERS.removed &&
    !hasLoadedRemovedSessions,
  );
  const isPhonePending = Boolean(
    SHOW_COMPANY_PHONE_IN_RECENTS && companyId && !hasLoadedPhoneCalls,
  );
  const isInitialRecentLoading =
    completedFilter === COMPLETED_FILTERS.removed
      ? isRemovedPending
      : completedFilter === COMPLETED_FILTERS.phone
        ? isPhonePending
        : completedFilter === COMPLETED_FILTERS.unlinked
          ? isHistoryPending
          : isHistoryPending || isPhonePending;

  // Recent is the only server-paginated view: fetch the next page when the
  // user scrolls near the bottom. Patients (local roster) and Scheduled load
  // in full, so they never trigger a fetch here.
  const isLoadingMoreCompleted = loadingMoreHistory || loadingMorePhoneCalls;
  const handleBrowserScroll = useCallback(() => {
    if (effectiveViewMode !== PLANNER_PATIENT_VIEW_MODES.completed) return;
    const node = scrollContainerRef.current;
    if (!node) return;
    const distanceFromBottom =
      node.scrollHeight - node.scrollTop - node.clientHeight;
    if (distanceFromBottom > SCROLL_LOAD_MORE_THRESHOLD_PX) return;
    const canLoadHistory = hasMoreHistory && !loadingMoreHistory;
    const canLoadPhone =
      SHOW_COMPANY_PHONE_IN_RECENTS &&
      hasMorePhoneCalls &&
      !loadingMorePhoneCalls;
    if (completedFilter === COMPLETED_FILTERS.phone) {
      if (canLoadPhone) loadMorePhoneCalls();
      return;
    }
    if (completedFilter === COMPLETED_FILTERS.unlinked) {
      if (canLoadHistory) loadMoreHistory();
      return;
    }
    if (canLoadHistory) loadMoreHistory();
    if (canLoadPhone) loadMorePhoneCalls();
  }, [
    completedFilter,
    effectiveViewMode,
    hasMoreHistory,
    hasMorePhoneCalls,
    loadMoreHistory,
    loadMorePhoneCalls,
    loadingMoreHistory,
    loadingMorePhoneCalls,
  ]);

  const renderModeControls = () => {
    const showSearchProgressBand = Boolean(
      isSearchOpen &&
      hasSearchQuery &&
      displaySearchResults.length > 0 &&
      isLoadingSearchResults,
    );
    if (showSearchProgressBand) {
      return <SearchProgressBand styles={styles} />;
    }

    if (
      isSearchOpen ||
      isResolvingPrimaryMode ||
      effectiveViewMode !== PLANNER_PATIENT_VIEW_MODES.completed
    )
      return null;

    return (
      <HStack
        data-testid="planner-recent-filter-bar"
        spacing={2}
        display="flex"
        flex="0 0 auto"
        w="100%"
        minW={0}
        overflowX="auto"
        overflowY="hidden"
        whiteSpace="nowrap"
        aria-label="Recent filter"
        sx={{
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {display: 'none'},
        }}
      >
        {completedFilterOptions.map(filter => (
          <FilterPillButton
            key={filter}
            value={filter}
            currentValue={completedFilter}
            label={completedFilterLabels[filter]}
            onClick={setCompletedFilter}
            styles={styles}
          />
        ))}
        <DateFilterPill
          filter={dateFilter}
          onChange={setDateFilter}
          styles={styles}
        />
      </HStack>
    );
  };

  const renderModeContent = () => {
    if (isSearchOpen && !hasSearchQuery) {
      return (
        <SearchEmptyState message="Start typing to search" styles={styles} />
      );
    }

    if (hasSearchQuery) {
      const showCombinedEmpty =
        !combinedSearchError && !isLoadingSearchResults && !hasAnySearchResults;
      const showSearchLoading =
        !combinedSearchError && isLoadingSearchResults && !hasAnySearchResults;

      if (showSearchLoading) {
        return (
          <SearchEmptyState isLoading message="Searching..." styles={styles} />
        );
      }

      if (showCombinedEmpty) {
        return <SearchEmptyState message="No results found" styles={styles} />;
      }

      return (
        <SearchPatientEncounterStack
          source="search"
          emptyText=""
          loading={
            (isSearchingEhrPatients || isLoadingCompanySearchEncounters) &&
            displaySearchResults.length === 0
          }
          error={combinedSearchError}
          selectedPatientId={selectedPatientId}
          searchQuery={deferredSearchQuery}
          onSelectPatient={handleSelectPatient}
          styles={styles}
          progressiveKey={`search-${effectiveViewMode}`}
          selectedEventId={selectedEventId}
          results={displaySearchResults}
          onSelectEvent={(event: any) => {
            onSelect?.(event);
          }}
        />
      );
    }

    if (isResolvingPrimaryMode) {
      return (
        <PlannerListSkeleton
          testId="planner-resolving-mode-spinner"
          ariaLabel="Loading"
          styles={styles}
        />
      );
    }

    if (
      effectiveViewMode === PLANNER_PATIENT_VIEW_MODES.patients &&
      canShowLocalPatientsMode
    ) {
      if (isLoadingLocalPatients && localPatientItems.length === 0) {
        return (
          <PlannerListSkeleton
            testId="planner-patients-loading-spinner"
            ariaLabel="Loading patients"
            showTimeColumn={false}
            styles={styles}
          />
        );
      }

      return (
        <>
          {localPatientsError && (
            <Text
              color={styles.errorTextColor}
              fontSize="md"
              fontWeight="normal"
              lineHeight="1.4"
              mb={3}
              aria-live="polite"
            >
              Could not load patients.
            </Text>
          )}
          <PatientSectionStack
            emptyText={localPatientsError ? '' : 'No patients yet.'}
            styles={styles}
            sections={localPatientBrowserSections}
            progressiveKey="patients"
            selectedPatientId={selectedPatientId}
            onSelectPatient={handleSelectPatient}
          />
        </>
      );
    }

    if (effectiveViewMode === PLANNER_PATIENT_VIEW_MODES.completed) {
      if (isInitialRecentLoading) {
        return (
          <PlannerListSkeleton
            testId="planner-recent-loading-skeleton"
            ariaLabel="Loading recent encounters"
            styles={styles}
          />
        );
      }
      const completedProgressiveKey = `completed-${completedFilter}-${
        dateFilter.startDate || 'start'
      }-${dateFilter.endDate || 'end'}`;
      const completedEmptyText =
        completedFilter === COMPLETED_FILTERS.unlinked
          ? 'No unlinked recent encounters.'
          : completedFilter === COMPLETED_FILTERS.phone
            ? 'No company phone calls.'
            : 'No recent encounters.';

      return (
        <>
          {completedFilter === COMPLETED_FILTERS.removed ? (
            <RemovedSessionsList
              events={removedEvents}
              pendingSessionIds={pendingRemovedSessionIds}
              onRestore={handleRestoreRemovedSession}
              styles={styles}
            />
          ) : completedFilter === COMPLETED_FILTERS.all ? (
            <RecentPatientEncounterStack
              events={completedAllRecentItems}
              emptyText={completedEmptyText}
              styles={styles}
              progressive={false}
              animateEntries={completedAllRecentItems.length <= 40}
              progressiveKey={completedProgressiveKey}
              selectedEventId={selectedEventId}
              selectedPatientId={selectedPatientId}
              onSelectEvent={(event: any) =>
                onSelect?.(markRecentEncounterDetailSelection(event))
              }
            />
          ) : (
            <DatePatientStack
              cardKind="recent"
              events={completedFilterEvents}
              emptyText={completedEmptyText}
              styles={styles}
              progressive={false}
              animateEntries={completedFilterEvents.length <= 40}
              progressiveKey={completedProgressiveKey}
              dateGrouping="past"
              sortDirection="desc"
              selectedEventId={selectedEventId}
              onSelect={(event: any) =>
                onSelect?.(markRecentEncounterDetailSelection(event))
              }
            />
          )}
          {completedFilter !== COMPLETED_FILTERS.removed &&
            isLoadingMoreCompleted && (
              <Flex
                data-testid="planner-load-more-spinner"
                role="status"
                aria-label="Loading more"
                aria-busy="true"
                align="center"
                justify="center"
                py={4}
                color="opalite.600"
              >
                <Spinner size="sm" thickness="2px" speed="0.7s" />
              </Flex>
            )}
        </>
      );
    }

    if (isInitialScheduleLoading) {
      return (
        <PlannerListSkeleton
          testId="planner-initial-loading-spinner"
          ariaLabel="Loading schedule"
          styles={styles}
        />
      );
    }

    // When the EHR schedule can't be auto-attributed to this user (e.g. a
    // resident, who never appears as the appointment provider), let them pick
    // which attending(s)' schedule to view instead of dumping the whole
    // facility list. The picker prompt replaces the list; once scoped, a
    // compact bar sits above it.
    const showScopeUi = Boolean(!isFallback && scheduleScope);
    const showScopePromptOnly = Boolean(
      showScopeUi && scheduleScope.needsManualSelection,
    );

    return (
      <>
        {statusMessage && (
          <Text
            color={error ? styles.errorTextColor : styles.metaTextColor}
            fontSize="md"
            fontWeight="normal"
            lineHeight="1.4"
            mb={3}
            aria-live="polite"
          >
            {statusMessage}
          </Text>
        )}
        {showScopeUi && (
          <PlannerScheduleScope scope={scheduleScope} styles={styles} />
        )}
        {/* Progressive mount: spread a long schedule's first render across
            animation frames so switching to Scheduled never blocks the main
            thread with one synchronous commit. preserveVisibleCountOnChange
            keeps already-mounted rows put when hydration refreshes the data,
            instead of collapsing back to the initial window. */}
        {!showScopePromptOnly && (
          <DatePatientStack
            cardKind="schedule"
            events={scheduleEvents}
            emptyText="No scheduled encounters."
            styles={styles}
            preserveVisibleCountOnChange
            progressiveKey="schedule"
            dateGrouping="future"
            sortDirection="asc"
            selectedEventId={selectedEventId}
            onSelect={(event: any) => onSelect?.(event)}
          />
        )}
      </>
    );
  };

  const isPullTracking = pullDistance > 0 && !isPullRefreshing;
  const pullRefreshActive = isPullRefreshing || pullDistance > 0;
  // Progress toward the trigger drives the fade/scale so the spinner emerges
  // with the gesture; it reaches full presence just before the arm point.
  const pullRefreshProgress = Math.min(
    1,
    pullDistance / PULL_REFRESH_TRIGGER_DISTANCE,
  );
  const pullRefreshIndicatorHeight = isPullRefreshing
    ? PULL_REFRESH_RESTING_DISTANCE
    : pullDistance;
  const pullRefreshIndicatorOpacity = isPullRefreshing
    ? 1
    : Math.min(1, pullRefreshProgress * 1.15);
  const pullRefreshSpinnerScale = isPullRefreshing
    ? 1
    : 0.7 + pullRefreshProgress * 0.3;
  const pullRefreshSpinnerOffset = isPullRefreshing
    ? 0
    : (1 - pullRefreshProgress) * 4;
  // While the finger is down the indicator follows it directly (no transition);
  // on release everything eases to its resting state together.
  const pullRefreshIndicatorTransition = isPullTracking
    ? 'none'
    : `height ${PULL_REFRESH_RELEASE_TRANSITION_MS}ms ${SEARCH_MOVE_EASE}, min-height ${PULL_REFRESH_RELEASE_TRANSITION_MS}ms ${SEARCH_MOVE_EASE}, opacity ${PULL_REFRESH_RELEASE_TRANSITION_MS}ms ${SEARCH_MOVE_EASE}, transform ${PULL_REFRESH_RELEASE_TRANSITION_MS}ms ${SEARCH_MOVE_EASE}`;

  // The opalite underlay shows through the white card's rounded corners on
  // the nav side only; its own right radius keeps the page-side notches
  // neutral.
  return (
    <GridItem
      area="browserColumn"
      display={containerDisplay}
      minW={0}
      minH={0}
      h="100%"
      position="relative"
      bg="transparent"
      _before={{
        content: '""',
        position: 'absolute',
        insetY: 0,
        left: 0,
        w: '50%',
        bg: 'opalite.600',
        borderRightRadius: 'xl',
        pointerEvents: 'none',
        // Blends the card's left corner notches into the blue nav rail; on
        // Compact layouts use the bottom tab bar, so the rail seam appears
        // only alongside the desktop/landscape-tablet rail.
        display: {base: 'none', lg: 'block'},
      }}
    >
      <Box
        position="relative"
        zIndex={1}
        h="100%"
        bg={styles.rowBg}
        borderRadius="xl"
        boxShadow={styles.cardShadow}
        overflow="hidden"
        px={{base: 4, lg: 5}}
        pt={{base: 4, lg: 5}}
        pb={0}
        display="flex"
        flexDirection="column"
        gap={3}
      >
        <Flex
          data-testid="planner-browser-controls"
          minH="44px"
          flex="0 0 auto"
          align="center"
          gap={1}
          flexWrap="nowrap"
          w="100%"
          minW={0}
          overflow="visible"
        >
          <Box
            data-testid="planner-mode-shell"
            data-modes-visible={isSearchOpen ? 'false' : 'true'}
            aria-hidden={isSearchOpen || undefined}
            flexGrow={isSearchOpen ? 0 : 1}
            flexShrink={1}
            flexBasis="0%"
            minW={0}
            // Clip vertically, but let a third mode tab scroll into reach on
            // phones instead of being silently cut off.
            overflowY="hidden"
            overflowX={{base: 'auto', md: 'hidden'}}
            sx={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': {display: 'none'},
            }}
            // -gap margin lets the expanded search reach the card's left edge
            // even though the zero-width mode shell still sits in the row.
            me={isSearchOpen ? `calc(${CONTROL_GAP} * -1)` : '0rem'}
            opacity={isSearchOpen ? 0 : 1}
            visibility={isSearchOpen ? 'hidden' : 'visible'}
            pointerEvents={isSearchOpen ? 'none' : 'auto'}
            transition={
              isSearchOpen
                ? `flex-grow ${SEARCH_MOVE_DURATION_MS}ms ${SEARCH_MOVE_EASE}, margin ${SEARCH_MOVE_DURATION_MS}ms ${SEARCH_MOVE_EASE}, opacity ${MODE_FADE_DURATION_MS}ms ease, visibility 0s linear ${MODE_FADE_DURATION_MS}ms`
                : `flex-grow ${SEARCH_MOVE_DURATION_MS}ms ${SEARCH_MOVE_EASE}, margin ${SEARCH_MOVE_DURATION_MS}ms ${SEARCH_MOVE_EASE}, opacity ${MODE_FADE_DURATION_MS}ms ease ${MODE_FADE_IN_DELAY_MS}ms, visibility 0s`
            }
          >
            <HStack
              spacing={{base: 5, md: 8}}
              display="inline-flex"
              flex="0 0 auto"
              overflow="visible"
              whiteSpace="nowrap"
            >
              {(primaryModeOptions || []).map(mode => (
                <TopModeButton
                  key={mode}
                  mode={mode}
                  currentMode={effectiveViewMode}
                  onClick={handleViewModeClick}
                  styles={styles}
                />
              ))}
            </HStack>
          </Box>

          <Box
            data-testid="planner-search-shell"
            data-search-expanded={isSearchOpen ? 'true' : 'false'}
            flexGrow={isSearchOpen ? 1 : 0}
            flexShrink={0}
            flexBasis="44px"
            minW="44px"
            overflow="hidden"
            transition={`flex-grow ${SEARCH_MOVE_DURATION_MS}ms ${SEARCH_MOVE_EASE}`}
          >
            {isSearchOpen ? (
              <InputGroup size="md">
                <Input
                  ref={searchInputRef}
                  value={searchQuery}
                  aria-label="Search patients and encounters"
                  placeholder="Search patients and encounters"
                  onFocus={activateSearch}
                  onChange={event => {
                    activateSearch();
                    setSearchQuery(event.target.value);
                  }}
                  bg={styles.rowBg}
                  borderColor={styles.rowBorderColor}
                  borderRadius="lg"
                  fontSize="md"
                  fontWeight="normal"
                  minH="44px"
                  pl={4}
                  pr={12}
                  _focus={styles.insetInputFocusStyles}
                  _focusVisible={styles.insetInputFocusStyles}
                />
                <InputRightElement h="100%" w="44px">
                  <IconButton
                    type="button"
                    size="sm"
                    boxSize="44px"
                    minW="44px"
                    variant="ghost"
                    borderRadius="lg"
                    bg="transparent"
                    color={styles.metaTextColor}
                    aria-label="Clear patient search"
                    icon={<TablerX size={16} />}
                    onClick={closeSearch}
                    _hover={{bg: 'transparent', color: styles.metaTextColor}}
                    _focus={{bg: 'transparent'}}
                    _active={{bg: 'transparent'}}
                    _focusVisible={styles.focusOutlineStyles}
                  />
                </InputRightElement>
              </InputGroup>
            ) : (
              <IconButton
                type="button"
                size="md"
                boxSize="44px"
                minW="44px"
                minH="44px"
                variant="ghost"
                borderRadius="lg"
                bg={styles.rowBg}
                color={styles.metaTextColor}
                borderWidth="1px"
                borderColor={styles.rowBorderColor}
                aria-label="Search patients"
                icon={<TablerSearch size={16} />}
                onClick={activateSearch}
                _hover={{
                  bg: styles.rowHoverBg,
                  color: styles.metaTextColor,
                }}
                _active={{bg: styles.rowActiveBg}}
                _focusVisible={styles.focusOutlineStyles}
              />
            )}
          </Box>

          {showNewMenu ? (
            <Menu placement="bottom-end" isLazy>
              <MenuButton
                as={IconButton}
                type="button"
                aria-label="New"
                icon={
                  <TablerPlus size={16} aria-hidden="true" focusable="false" />
                }
                size="md"
                boxSize="44px"
                minW="44px"
                minH="44px"
                px={0}
                flexShrink={0}
                borderRadius="lg"
                variant="ghost"
                bg="opalite.600"
                color="white"
                borderWidth="1px"
                borderColor="opalite.600"
                fontSize={PLANNER_SIDEBAR_NAME_FONT_SIZE}
                fontWeight="normal"
                lineHeight="1.25"
                _hover={{
                  bg: 'opalite.700',
                  color: 'white',
                }}
                _active={{bg: 'opalite.700'}}
                _expanded={{bg: 'opalite.700'}}
                _focusVisible={styles.focusOutlineStyles}
              />
              <MenuList
                minW="12rem"
                bg={styles.rowBg}
                borderColor={styles.rowBorderColor}
                boxShadow={styles.cardShadow}
                py={1}
              >
                {onNewEncounter && (
                  <MenuItem
                    icon={<TablerPlus size={16} aria-hidden="true" />}
                    fontSize="md"
                    onClick={onNewEncounter}
                  >
                    New Encounter
                  </MenuItem>
                )}
                {onNewTelephoneEncounter && (
                  <MenuItem
                    icon={<TablerPhone size={16} aria-hidden="true" />}
                    fontSize="md"
                    onClick={onNewTelephoneEncounter}
                  >
                    New Telephone Encounter
                  </MenuItem>
                )}
                {onNewDictation && (
                  <MenuItem
                    icon={<TablerMicrophone size={16} aria-hidden="true" />}
                    fontSize="md"
                    onClick={onNewDictation}
                  >
                    Dictation
                  </MenuItem>
                )}
                {canCreatePatient && (
                  <MenuItem
                    icon={<TablerUsers size={16} aria-hidden="true" />}
                    fontSize="md"
                    onClick={openCreateModal}
                  >
                    New Patient
                  </MenuItem>
                )}
              </MenuList>
            </Menu>
          ) : onNewEncounter ? (
            <IconButton
              type="button"
              aria-label="New"
              icon={
                <TablerPlus size={16} aria-hidden="true" focusable="false" />
              }
              onClick={onNewEncounter}
              size="md"
              boxSize="44px"
              minW="44px"
              minH="44px"
              px={0}
              flexShrink={0}
              borderRadius="lg"
              variant="ghost"
              bg="opalite.600"
              color="white"
              borderWidth="1px"
              borderColor="opalite.600"
              fontSize={PLANNER_SIDEBAR_NAME_FONT_SIZE}
              fontWeight="normal"
              lineHeight="1.25"
              _hover={{
                bg: 'opalite.700',
                color: 'white',
              }}
              _active={{bg: 'opalite.700'}}
              _focusVisible={styles.focusOutlineStyles}
            />
          ) : null}
        </Flex>

        <AnimatePresence initial={false}>
          {continueCandidate &&
            continueCandidate.id !== dismissedContinueId &&
            !isSearchOpen &&
            effectiveViewMode !== PLANNER_PATIENT_VIEW_MODES.completed && (
              <PlannerContinueSessionBanner
                key="planner-continue-session-banner"
                encounter={continueCandidate}
                isContinuing={isContinuing}
                onContinue={(encounter: any) => continueEncounter(encounter)}
                onDismiss={(encounter: any) =>
                  setDismissedContinueId(encounter?.id || null)
                }
                styles={styles}
              />
            )}
        </AnimatePresence>

        {/* Guided onboarding progress for /start sign-ins — renders null for
            everyone else (visibility is decided by the backend). */}
        <UniversitySafetyBoundary>
          <UniversityGettingStartedCard />
        </UniversitySafetyBoundary>

        {renderModeControls()}

        <Box
          ref={scrollContainerRef}
          as="section"
          aria-label="Planner schedule and patients"
          data-testid="planner-column-scroll"
          data-pull-refreshing={isPullRefreshing ? 'true' : 'false'}
          flex={1}
          minW={0}
          minH={0}
          overflowY="auto"
          // Contain the scroll only where this column is the bounded scroller:
          // phones (schedule-only full-height screen) and lg+ (side-by-side).
          overscrollBehavior="contain"
          pb="2em"
          sx={scrollbarSx}
          onScroll={handleBrowserScroll}
          onTouchStart={handlePullTouchStart}
          onTouchEnd={handlePullTouchEnd}
          onTouchCancel={resetPullDistance}
        >
          {compactHeader}
          <Flex
            data-testid="planner-pull-refresh-indicator"
            data-visible={pullRefreshActive ? 'true' : 'false'}
            h={`${pullRefreshIndicatorHeight}px`}
            minH={`${pullRefreshIndicatorHeight}px`}
            align="center"
            justify="center"
            color="opalite.600"
            opacity={pullRefreshIndicatorOpacity}
            transform={`translate3d(0, ${pullRefreshSpinnerOffset.toFixed(2)}px, 0) scale(${pullRefreshSpinnerScale.toFixed(3)})`}
            transition={pullRefreshIndicatorTransition}
            willChange="height, min-height, opacity, transform"
            overflow="hidden"
            pointerEvents="none"
            aria-hidden="true"
          >
            <Spinner size="sm" thickness="2px" speed="0.7s" />
          </Flex>
          <Box display="grid" h="100%" minW={0} minH="100%">
            {/* Avoid cross-rendering search results with a newly selected mode during fast clear/tab clicks. */}
            {shouldAnimateBrowserPage ? (
              <AnimatePresence initial={false} mode="wait">
                <BrowserPageTransitionPane
                  key={browserPageKey}
                  pageKey={browserPageKey}
                >
                  {renderModeContent()}
                </BrowserPageTransitionPane>
              </AnimatePresence>
            ) : (
              <Box
                key={browserPageKey}
                data-testid="planner-browser-page"
                data-browser-page={browserPageKey}
                gridArea="1 / 1"
                h="100%"
                minW={0}
                minH="100%"
              >
                {renderModeContent()}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <PatientFormModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="New Patient"
        formData={formData}
        setFormData={setFormData}
        onSave={handleCreatePatient}
        saving={isCreatingPatient}
        saveLabel="Create Patient"
        duplicateMatch={duplicateMatch}
        onUseExisting={chooseExistingPatient}
        onCreateAnyway={confirmDuplicateCreate}
      />
      {continueModePicker}
    </GridItem>
  );
};

export default PlannerBrowserColumn;
